import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import { makeStyles } from '@material-ui/core';
import { gql } from 'apollo-boost';
import cn from 'classnames';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '~components/button';
import Loading from '~components/button/loading';
import Input from '~components/input/input';
import Typography from '~components/typography';
import ExternalIdChip from '~containers/global-search-bar/external-id-chip';
import {
  PLACEHOLDER_STUDENT,
  STUDENT_WITH_DETAIL,
} from '~containers/nomination-manage-students/add-student-to-nomination-plan-modal/create-student-modal-content';
import StudentAvatarWithStatus from '~containers/student/detail/student-avatar-with-status';
import GenderIcon from '~containers/student/list/student-list-gender-icon';
import { useReplaceSuiteStudentToCustomer } from '~containers/suite-config/suite-context';
import { BasicClose, BasicSearch, BasicStudentWhoBig } from '~svg-components';
import { useDebounce } from '~utils/use-effect';
import useHandleError from '~utils/use-handle-error';

const FRAGMENT_STUDENT = gql`
  fragment FragmentStudent on Student {
    account {
      id
      emailVerified
    }
    bookings: baseBookings(propertyId: $propertyId) {
      id
      nominationPlan {
        id
      }
      partnerReferenceId
      status
    }
    customFieldValues {
      id
      labelName
      value
    }
    email
    externalId
    fullName
    gender
    generalInfo {
      basic {
        dateOfBirth
        nationality
      }
      education {
        destinationUniversity
      }
      contact {
        mobilePhoneAreaCode
        mobilePhoneNumber
        countryTerritory
      }
    }
    id
    isRebooker
    originId
    profilePhotoFileUuid
  }
`;

const QUERY_GET_GLOBAL_SEARCH_STUDENT_RESULT = gql`
  query QueryGetGlobalSearchStudentResult($searchValue: NonEmptyString!, $propertyId: ID!) {
    getStudentSearchResult(searchValue: $searchValue, propertyId: $propertyId) {
      students {
        ...FragmentStudent
      }
    }
  }
  ${FRAGMENT_STUDENT}
`;

const QUERY_STUDENTS = gql`
  query QueryStudents(
    $propertyId: ID!
    $originIdLike: String
    $nameLike: String
    $roomLocation: getStudentsRoomLocationOptions = ALL
    $pageNumber: Int = 1
    $pageSize: Int = 5
  ) {
    getStudents(
      propertyId: $propertyId
      originIdLike: $originIdLike
      nameLike: $nameLike
      roomLocation: $roomLocation
      pageNumber: $pageNumber
      pageSize: $pageSize
    ) {
      students {
        ...FragmentStudent
      }
    }
  }
  ${FRAGMENT_STUDENT}
`;

export const CREATE_STUDENT_USER = gql`
  mutation CreateStudentUser(
    $propertyId: ID!
    $email: String!
    $firstName: String!
    $middleName: String
    $lastName: String!
    $gender: GenderOptions
    $heardSource: String
    $preferredLanguage: String
  ) {
    createStudentUser(
      propertyId: $propertyId
      email: $email
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      gender: $gender
      heardSource: $heardSource
      preferredLanguage: $preferredLanguage
    ) {
      id
      studentInfo {
        ...FragmentStudent
      }
    }
  }
  ${FRAGMENT_STUDENT}
`;

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    padding: '0 16px 136px',
    width: '560px',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  active: {
    animation: '0.3s $scale forwards',
    paddingBottom: 56,
  },

  '@keyframes scale': {
    from: { height: 288 },
    to: { height: 516 },
  },
  avatar: {
    position: 'relative',
    width: '120px',
    height: '120px',
  },

  avatarIcon: {
    width: '100%',
    height: '100%',
  },

  avatarDecorativeIconWrap: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    boxShadow: theme.shadow.componentDark,
    backgroundColor: theme.colors.violet,
    borderRadius: '50%',
  },

  avatarDecorativeIcon: {
    width: '15px',
    height: '27px',
    fill: theme.colors.white,
  },

  title: {
    ...theme.flex.center,
    height: 88,
    opacity: 1,
  },
  titleHidden: {
    height: 8,
    opacity: 0,
    transition: 'opacity 0.3s, top 0.3s',
  },

  search: {
    width: '400px',
    height: '48px',
    margin: '8px 0 0',
    border: 0,
    borderRadius: 24,
    backgroundColor: `${theme.colors.greyF7} !important`,
    boxShadow: 'none',
  },

  searchInput: {
    paddingLeft: '36px',
  },

  leftAction: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '36px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 13,
  },

  rightAction: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '48px',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 18,
    cursor: 'pointer',
  },
  searchIcon: {
    width: 16,
    height: 16,
    fill: theme.colors.grey99,
  },
  closeIcon: {
    width: 12,
    height: 12,
    fill: theme.colors.grey66,
  },

  studentAvator: {
    position: 'absolute',
    height: 40,
    width: 40,
    left: 12,
    top: 16,
  },

  loading: {
    height: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  iconArrow: {
    width: 12,
    height: 12,
    fill: theme.colors.brandIcon,
  },
  bgIcon: {
    position: 'absolute',
    bottom: 20,
    right: 24,
    width: 80,
    height: 80,
    fill: theme.colors.brandBg,
  },
  container: {
    textAlign: 'center',
  },
  containerTop: {
    margin: 0,
    transition: 'margin 0.3s',
  },
  contentContainer: {
    paddingTop: 8,
    zIndex: 10,
    width: 400,
    height: 404,
    opacity: 0,
  },
  contentContainerShow: {
    animationDelay: '0.5s',
    animation: '0.3s $show forwards',
  },
  '@keyframes show': {
    from: { opacity: 0, transform: 'translateY(8px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
  },
  resultsList: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    opacity: 0,
    height: '100%',
  },
  show: {
    opacity: 1,
    transition: 'opacity 0.3s',
  },
  result: {
    position: 'relative',
    padding: '12px 12px 11px',
    borderBottom: `1px solid ${theme.colors.greyE8}`,
    cursor: 'pointer',
    width: 400,
  },
  resultRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 4,
    alignItems: 'baseline',
    paddingLeft: 52,

    '&:last-child': {
      marginBottom: 0,
    },
  },
  id: {
    display: 'flex',
    alignItems: 'center',
    color: theme.colors.grey99,
  },
  unverifiedLabel: {
    padding: '0 8px',
    borderRadius: 20,
    backgroundColor: theme.colors.orangeBg,
    color: theme.colors.orangeDark,
  },
  rebookerLabel: {
    padding: '0 8px',
    borderRadius: 20,
    backgroundColor: theme.colors.roseBg,
    color: theme.colors.roseDark,
  },
  studentIcon: {
    height: 40,
    width: 40,
    fill: theme.colors.brandIcon,
  },
  notFound: {
    textAlign: 'left',
    padding: '16px 12px 56px',
    color: theme.colors.grey66,
  },
  createNew: {
    marginLeft: 4,
    color: theme.colors.brandAction,
    cursor: 'pointer',
  },
  createNominationNew: {
    color: theme.colors.brandAction,
    cursor: 'pointer',
  },
  email: {
    maxWidth: 240,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  notFoundRow: {
    padding: 0,
    margin: 0,
  },
  genderIcon: {
    marginLeft: 4,
    transform: 'translateY(3px)',
  },
  name: {
    maxWidth: 245,
    wordBreak: 'break-all',
  },
  infoIcon: {
    height: 12,
    width: 12,
    marginLeft: 4,
  },
  externalId: {
    marginRight: 8,
  },
  placeholderStudentRoot: {
    ...theme.flex.mainStart,
    ...theme.flex.crossCenter,
    width: 400,
    marginTop: 24,
    marginLeft: 24,
  },
  placeholderBtn: {
    ...theme.typography.body2,
    color: theme.colors.brandAction,
    marginLeft: 4,
  },
}));

function StepFindStudent(props) {
  const {
    title,
    propertyId,
    defaultSearchValue,
    defaultShowResultList,
    onClickStudent,
    onCreateNewStudent,
    isNominationCreateStudent = false,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation('booking');
  const replaceSuiteStudentToCustomer = useReplaceSuiteStudentToCustomer();

  const [searchValue, setSearchValue] = useState(defaultSearchValue ?? '');
  const debounceSearchValue = useDebounce(searchValue);
  const [showResultList, setShowResultList] = useState(defaultShowResultList ?? false);

  const { data: queryStudents, loading: queryStudentsLoading } = useQuery(QUERY_STUDENTS, {
    fetchPolicy: 'no-cache',
    variables: { propertyId },
  });
  const queryStudentsData = useMemo(
    () =>
      queryStudents?.getStudents?.students.map((student) => ({
        ...student,
        emailVerified: student?.account?.emailVerified,
        contactEmail: student?.email,
        bookingStatus: student?.bookings.map((booking) => booking?.status),
      })),
    [queryStudents]
  );

  const [searchStudent, { data: searchStudentData, error, loading }] = useLazyQuery(
    QUERY_GET_GLOBAL_SEARCH_STUDENT_RESULT,
    {
      fetchPolicy: 'network-only',
    }
  );

  useHandleError(error);

  useEffect(() => {
    if (debounceSearchValue && debounceSearchValue.trim() && propertyId) {
      searchStudent({
        variables: { searchValue: debounceSearchValue.trim(), propertyId },
      });
    }
  }, [debounceSearchValue, propertyId, searchStudent]);

  const handleClear = () => {
    setSearchValue('');
  };

  const renderSearchStudentResults = (results) => {
    return (
      <ul className={cn(classes.resultsList, showResultList && classes.show)}>
        {results &&
          results.map((record, index) => (
            <li
              className={classes.result}
              key={index}
              onClick={() => {
                if (onClickStudent) {
                  onClickStudent({ student: record, searchValue: debounceSearchValue });
                }
              }}
            >
              <StudentAvatarWithStatus
                src={record.profilePhotoFileUuid}
                className={classes.studentAvator}
                bookings={record.bookings}
                StatusIconProps={{
                  size: 24,
                }}
              />

              <Typography component="div" className={classes.resultRow}>
                <Typography
                  variant="body1"
                  themeColor="dark"
                  component="span"
                  className={classes.name}
                >
                  {record.fullName}
                  <GenderIcon gender={record.gender} className={classes.genderIcon} />
                </Typography>
                {!record?.account?.emailVerified && (
                  <Typography variant="body2" component="span" className={classes.unverifiedLabel}>
                    {t('unverified')}
                  </Typography>
                )}
                {record?.account?.emailVerified && record.isRebooker && (
                  <Typography variant="body2" component="span" className={classes.rebookerLabel}>
                    {t('rebooker')}
                  </Typography>
                )}
              </Typography>
              <Typography component="div" className={classes.resultRow}>
                <Typography
                  variant="body2"
                  themeColor="grey66"
                  component="span"
                  className={classes.email}
                >
                  {record?.email}
                </Typography>
                <ExternalIdChip cardId={record.originId} externalIds={record.customFieldValues} />
              </Typography>
            </li>
          ))}

        {searchValue !== '' && !isNominationCreateStudent && (
          <li className={classes.notFoundRow} key="no_found">
            <Typography variant="body2" className={classes.notFound}>
              <Typography variant="body2" component="span" themeColor="grey66">
                {t('no_match_found')}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                className={classes.createNew}
                onClick={() => onCreateNewStudent({ searchValue: debounceSearchValue })}
              >
                {replaceSuiteStudentToCustomer(t('create_new_student'), t('create_new_customer'))}
              </Typography>
            </Typography>
          </li>
        )}

        {searchValue !== '' && isNominationCreateStudent && (
          <li className={classes.notFoundRow} key="no_found">
            <div className={classes.notFound}>
              <div style={{ marginBottom: 8 }}>
                <Typography variant="body2" component="span" themeColor="grey66">
                  {t('no_match_found')}
                </Typography>
              </div>
              <div style={{ marginTop: 4 }}>
                <Typography
                  variant="body2"
                  component="span"
                  className={classes.createNominationNew}
                  onClick={() =>
                    onCreateNewStudent({
                      searchValue: debounceSearchValue,
                      createType: STUDENT_WITH_DETAIL,
                    })
                  }
                >
                  {t('create_a_student_with_detail')}
                </Typography>
              </div>
              <div style={{ marginTop: 4 }}>
                <Typography
                  variant="body2"
                  component="span"
                  className={classes.createNominationNew}
                  onClick={() =>
                    onCreateNewStudent({
                      searchValue: debounceSearchValue,
                      createType: PLACEHOLDER_STUDENT,
                    })
                  }
                >
                  {replaceSuiteStudentToCustomer(
                    t('create_a_placeholder_student'),
                    t('create_a_placeholder_customer')
                  )}
                </Typography>
              </div>
            </div>
          </li>
        )}
      </ul>
    );
  };

  return (
    <div className={cn(classes.root, showResultList && !defaultShowResultList && classes.active)}>
      <BasicStudentWhoBig className={classes.bgIcon} />
      <div className={cn(classes.container, showResultList && classes.containerTop)}>
        <Typography
          variant="h2"
          align="center"
          themeColor="dark"
          className={cn(classes.title, showResultList && classes.titleHidden)}
        >
          {title}
        </Typography>

        <Input
          onFocus={() => {
            if (!showResultList) setShowResultList(true);
          }}
          onChange={(e) => {
            setSearchValue(e.target.value);
          }}
          classes={{
            root: cn(classes.search),
            input: classes.searchInput,
          }}
          value={searchValue}
          placeholder={replaceSuiteStudentToCustomer(
            t('search_placeholder'),
            t('search_placeholder.CUSTOMER')
          )}
          startAdornment={
            <div className={classes.leftAction}>
              <BasicSearch className={classes.searchIcon} />
            </div>
          }
          endAdornment={
            searchValue && (
              <div className={classes.rightAction} onClick={handleClear}>
                <BasicClose className={classes.closeIcon} />
              </div>
            )
          }
        />
      </div>

      {showResultList && (
        <div
          className={cn(classes.contentContainer, showResultList && classes.contentContainerShow)}
        >
          {loading || queryStudentsLoading ? (
            <div className={classes.loading}>
              <Loading color={'brandMedium'} />
            </div>
          ) : (
            renderSearchStudentResults(
              searchValue === ''
                ? queryStudentsData
                : searchStudentData?.getStudentSearchResult?.students
            )
          )}
        </div>
      )}
      {isNominationCreateStudent && !searchValue && (
        <div className={classes.placeholderStudentRoot}>
          <Typography variant="body2" themeColor="grey66">
            {t('don_t_have_any_info')}
          </Typography>
          <Button
            className={classes.placeholderBtn}
            variant="text"
            onClick={() => onCreateNewStudent({ searchValue: debounceSearchValue })}
          >
            {replaceSuiteStudentToCustomer(
              t('create_a_placeholder_student'),
              t('create_a_placeholder_customer')
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

StepFindStudent.propTypes = {
  title: PropTypes.node.isRequired,
  propertyId: PropTypes.string.isRequired,
  defaultSearchValue: PropTypes.string,
  defaultShowResultList: PropTypes.bool,
  onClickStudent: PropTypes.func,
  onCreateNewStudent: PropTypes.func,
  isNominationCreateStudent: PropTypes.bool,
};

export default StepFindStudent;
